import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoChevronBack, IoNotificationsOutline, IoTrashOutline } from 'react-icons/io5';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import useRole from '../../hooks/useRole';

const TYPE_CONFIG = {
  newOrder: { label: '주문', color: '#3B6DAA', path: '/orders' },
  orderStatus: { label: '주문', color: '#3B6DAA', path: '/orders' },
  newSchedule: { label: '일정', color: '#4A6741', path: '/schedule' },
  scheduleChange: { label: '일정', color: '#4A6741', path: '/schedule' },
  newUser: { label: '회원', color: '#6B5B7B', path: '/admin/users' },
  userApproval: { label: '승인', color: '#C5A55A', path: '/home' },
  newNotice: { label: '공지', color: '#E85830', path: '/board' },
  newChat: { label: '채팅', color: '#2C3640', path: '/chat' },
};

const NotificationPage = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { isGeneral } = useRole();
  const myUid = userData?.uid;
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!myUid) return;
    const q = query(
      collection(db, 'notifications'),
      where('targetUids', 'array-contains', myUid),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setNotifications(list);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [myUid]);

  const handleClick = async (noti) => {
    // readBy에 추가
    if (!noti.readBy?.includes(myUid)) {
      try {
        await updateDoc(doc(db, 'notifications', noti.id), {
          readBy: arrayUnion(myUid),
        });
      } catch {}
    }
    // 해당 페이지로 이동 (일반 역할은 일정 페이지 이동 방지)
    const config = TYPE_CONFIG[noti.type];
    if (isGeneral && (noti.type === 'newSchedule' || noti.type === 'scheduleChange')) return;
    if (config?.path) navigate(config.path);
  };

  const handleDelete = async (e, noti) => {
    e.stopPropagation();
    try {
      await updateDoc(doc(db, 'notifications', noti.id), {
        targetUids: arrayRemove(myUid),
      });
    } catch (err) {
      console.error('알림 삭제 실패:', err);
    }
  };

  const formatTime = (ts) => {
    if (!ts?.toDate) return '';
    const d = ts.toDate();
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return '방금 전';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}시간 전`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}일 전`;
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  return (
    <Container>
      <Header>
        <BackBtn onClick={() => navigate(-1)}>
          <IoChevronBack size={22} />
        </BackBtn>
        <HeaderTitle>알림</HeaderTitle>
      </Header>

      <Content>
        {loading ? (
          <Empty>로딩 중...</Empty>
        ) : notifications.length === 0 ? (
          <EmptyWrap>
            <IoNotificationsOutline size={28} color="#D0D0D0" />
            <Empty>알림이 없습니다</Empty>
          </EmptyWrap>
        ) : (
          notifications.map(noti => {
            const isRead = noti.readBy?.includes(myUid);
            const config = TYPE_CONFIG[noti.type] || { label: '알림', color: '#8C9AA4' };
            return (
              <NotiItem key={noti.id} $read={isRead} onClick={() => handleClick(noti)}>
                {!isRead && <UnreadDot />}
                <NotiBody>
                  <NotiTop>
                    <TypeBadge $color={config.color}>{config.label}</TypeBadge>
                    <NotiTopRight>
                      <NotiTime>{formatTime(noti.createdAt)}</NotiTime>
                      <DeleteBtn onClick={(e) => handleDelete(e, noti)}>
                        <IoTrashOutline size={15} />
                      </DeleteBtn>
                    </NotiTopRight>
                  </NotiTop>
                  <NotiTitle $read={isRead}>{noti.title}</NotiTitle>
                  {noti.body && <NotiDesc>{noti.body}</NotiDesc>}
                </NotiBody>
              </NotiItem>
            );
          })
        )}
      </Content>
    </Container>
  );
};

export default NotificationPage;

const Container = styled.div`
  min-height: 100vh;
  background: #F4F3F8;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 8px;
  background: white;
  border-bottom: 1px solid #F0F0EC;
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  color: #2C3640;
  padding: 4px;
`;

const HeaderTitle = styled.p`
  font-size: 17px;
  font-weight: 500;
  color: #2C3640;
`;

const Content = styled.div`
  padding: 8px 0;
`;

const EmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 16px;
  min-height: calc(80vh - 52px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

const Empty = styled.p`
  text-align: center;
  color: #8C9AA4;
  font-size: 14px;
`;

const NotiItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 16px 20px;
  background: ${p => p.$read ? '#fff' : '#FAFBFF'};
  border-bottom: 1px solid #F0F0EC;
  cursor: pointer;
  &:active { background: #F0F0EC; }
`;

const UnreadDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #E85830;
  flex-shrink: 0;
  margin-top: 6px;
`;

const NotiBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotiTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const NotiTopRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DeleteBtn = styled.button`
  display: flex;
  align-items: center;
  padding: 4px;
  color: #C0C4C8;
  border-radius: 4px;
  &:active { color: #EF5350; background: #FFF0F0; }
`;

const TypeBadge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${p => p.$color}15;
  color: ${p => p.$color};
  font-weight: 500;
`;

const NotiTime = styled.span`
  font-size: 11px;
  color: #B0B8C0;
`;

const NotiTitle = styled.p`
  font-size: 14px;
  font-weight: ${p => p.$read ? 400 : 500};
  color: #1E2A32;
  margin-bottom: 2px;
`;

const NotiDesc = styled.p`
  font-size: 13px;
  color: #8C9AA4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
